import React, { Component, ReactNode } from 'react';
import { ErrorTypes, Illustration } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { noop } from '@fabric-ui/utils';
import { Box, Button, ButtonProps, SxProps, Theme, Typography } from '@mui/material';
import { useNavigate } from '@utils/AvRouter';

const notFoundStyle: SxProps<Theme> = {
  '@keyframes fadeIn': {
    '0%': { opacity: 0 },
    '100%': { opacity: 1 },
  },
  animation: 'fadeIn 0.5s',
  ...flex.colCenter,
  textAlign: 'center',
  gap: '24px',
  backgroundColor: theme => theme.palette.background.default,
  h3: {
    color: 'var(--color-content-base-secondary)',
    marginBottom: 0,
  },
};
// TODO merge with NoDataFound component

export const isModuleNotFoundError = (error: any): boolean =>
  typeof error?.message === 'string' && /Failed to fetch dynamically imported module/.test(error.message);

interface NotFoundProps {
  text?: string;
  image?: string | ReactNode;
  buttonProps?: ButtonProps;
}

export function NotFound({ text = 'Page Not Found', image = <Illustration.Medium.Issue />, buttonProps = {} }: NotFoundProps) {
  const navigate = useNavigate();
  const navi = () =>
    navigate({
      from: '',
      to: '/$accountId',
      params: ({ accountId }) => ({ accountId }),
      ignoreBlocker: true,
    });
  return (
    <main style={{ paddingBottom: '20px' }}>
      <Box sx={{ ...notFoundStyle, height: '100%' }}>
        {typeof image === 'string' ? (
          <img src={image} alt="not found" style={{ marginBottom: '8px' }} />
        ) : (
          <Box sx={{ height: 340, aspectRatio: '1', ...flex.center, mb: 1, '& svg': { width: '100%', height: '100%' } }}>{image}</Box>
        )}
        <Typography variant="h3">{text}</Typography>
        <Button
          variant="text"
          onClick={navi}
          sx={{ color: theme => theme.palette?.primary?.main || 'inherit', fontWeight: 600 }}
          {...buttonProps}>
          {buttonProps.children || 'Return to Homepage'}
        </Button>
      </Box>
    </main>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  location: string;
  fallbackComponent?: ReactNode;
  onError: () => void;
}

interface ErrorBoundaryState {
  errorType?: (typeof ErrorTypes)[keyof typeof ErrorTypes];
}
// eslint-disable-next-line fabric-ui/no-class
class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { errorType: undefined };
  }

  componentDidUpdate(prevProps, prevState) {
    const { location } = this.props;
    const { errorType } = prevState;
    if (errorType && prevProps.location !== location) {
      this.setState(({ errorType, ...prev }) => prev);
    }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    if (error.message !== ErrorTypes.AvContext) {
      console.error(error);
    }
    return { errorType: error.message };
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    console.error(error, errorInfo);
    this.props.onError();
  }

  render() {
    const { errorType } = this.state;
    const { children, fallbackComponent } = this.props;

    if (errorType) {
      return (
        fallbackComponent || (
          <NotFound
            text="Something went wrong.."
            image={<Illustration.Medium.Issue />}
            buttonProps={{
              variant: 'contained',
              children: 'Refresh to reload',
              onClick: () => window.location.reload(),
              sx: { fontWeight: 600 },
            }}
          />
        )
      );
    }
    return children;
  }
}

export function ErrorBoundary({ children, onError = noop, ...props }) {
  return (
    <ErrorBoundaryClass location={window.location.href} onError={onError} {...props}>
      {children}
    </ErrorBoundaryClass>
  );
}
